import websocketService from '../services/websocketService';
import { clearAuth, resetApiClientState, getApiBaseUrl } from '../services/api';
import { clearSessionCookie } from './siteHosts';

export const appSessionResetEventName = 'appSessionReset';

/** localStorage keys to preserve across session resets (e.g. theme preference, AI assistant configuration). */
const PRESERVED_KEYS = [
  'unified-workforce-theme',
  'workfolio-theme',
  'unified_workforce_gemini_key',
  'unified_workforce_ai_chats',
  'unified_workforce_ai_selected_model',
];

/**
 * Hard reset of client-side session state to prevent cross-user data leakage.
 * Ensures: token, refresh_token, user, registeredUsers removed; WebSocket stopped;
 * API client reset; components listening to appSessionResetEventName re-initialize.
 * Preserves theme preference if present.
 */
export function resetAppSession(reason = 'unspecified') {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[session] resetAppSession', reason);
  }

  if (reason === 'logout') {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const refresh = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
      if (token || refresh) {
        const baseUrl = getApiBaseUrl();
        const logoutUrl = `${baseUrl.replace(/\/+$/, '')}/auth/logout/`;
        const payload = JSON.stringify({ token, refresh });
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(logoutUrl, blob);
        } else if (typeof fetch === 'function') {
          fetch(logoutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      }
    } catch (_) {
      // ignore
    }
  }

  try {
    websocketService.stop();
  } catch (_) {
    // ignore
  }

  try {
    resetApiClientState();
  } catch (_) {
    // ignore
  }

  try {
    clearAuth();
  } catch (_) {
    // ignore
  }

  try {
    clearSessionCookie();
  } catch (_) {
    // ignore
  }

  try {
    const preserved = {};
    for (const key of PRESERVED_KEYS) {
      const val = localStorage.getItem(key);
      if (val != null) preserved[key] = val;
    }
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('unified_workforce_payroll_')) preserved[key] = localStorage.getItem(key);
    }
    localStorage.clear();
    for (const [key, val] of Object.entries(preserved)) {
      localStorage.setItem(key, val);
    }
  } catch (_) {
    // ignore
  }

  try {
    sessionStorage.clear();
  } catch (_) {
    // ignore
  }

  try {
    window.dispatchEvent(new CustomEvent(appSessionResetEventName, { detail: { reason } }));
  } catch (_) {
    // ignore
  }

  try {
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: null }));
  } catch (_) {
    // ignore
  }
}
